import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Collapse, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import './AdminTenants.css';

interface TenantLimits {
  maxCloudAccounts: number;
  maxReportsPerMonth: number;
}

interface TenantFeatures {
  aiEnabled: boolean;
}

interface TenantRecord {
  tenantId: string;
  name: string;
  authorizedDomains: string[];
  plan: string;
  limits: TenantLimits;
  features: TenantFeatures;
  createdAt: string;
  updatedAt: string;
}

interface PlanPreset {
  name: string;
  limits: TenantLimits;
  features: TenantFeatures;
}

type PlanCatalog = Record<string, PlanPreset>;

const dateFormatter = (value?: string) => {
  if (!value) return '-';
  return new Date(value).toLocaleString('pt-BR');
};

const planColor = (plan?: string) => {
  switch (plan) {
    case 'standard':
      return 'gold';
    case 'free':
      return 'blue';
    default:
      return 'default';
  }
};

const AdminTenants: React.FC = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [tenants, setTenants] = useState<TenantRecord[]>([]);
  const [planCatalog, setPlanCatalog] = useState<PlanCatalog>({});
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTenant, setEditingTenant] = useState<TenantRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isPlatformAdmin = useMemo(
    () => user?.roles?.some(role => role.toLowerCase() === 'platform-admin'),
    [user?.roles]
  );

  const planOptions = useMemo(
    () =>
      Object.entries(planCatalog).map(([id, preset]) => ({
        value: id,
        label: preset.name
      })),
    [planCatalog]
  );

  const loadPlanCatalog = useCallback(async () => {
    if (!isPlatformAdmin) return;

    try {
      const response = await apiClient.get('/tenants/plans/catalog');
      setPlanCatalog(response.data?.presets || {});
    } catch (catalogError: any) {
      message.error(catalogError.response?.data?.message || 'Não foi possível carregar o catálogo de planos.');
    }
  }, [isPlatformAdmin]);

  const loadTenants = useCallback(async () => {
    if (!isPlatformAdmin) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/tenants');
      setTenants(response.data?.tenants || []);
    } catch (loadError: any) {
      setError(loadError.response?.data?.message || 'Não foi possível carregar os tenants.');
    } finally {
      setLoading(false);
    }
  }, [isPlatformAdmin]);

  useEffect(() => {
    loadPlanCatalog();
    loadTenants();
  }, [loadPlanCatalog, loadTenants]);

  const handleCreateTenant = async (values: any) => {
    setCreating(true);

    try {
      await apiClient.post('/tenants', {
        tenantId: values.tenantId,
        name: values.name,
        authorizedDomains: values.authorizedDomains || [],
        planPreset: values.planPreset
      });

      message.success('Tenant criado com sucesso.');
      form.resetFields();
      await loadTenants();
    } catch (createError: any) {
      message.error(createError.response?.data?.message || 'Não foi possível criar o tenant.');
    } finally {
      setCreating(false);
    }
  };

  const openEditModal = (record: TenantRecord) => {
    setEditingTenant(record);
    editForm.setFieldsValue({
      name: record.name,
      authorizedDomains: record.authorizedDomains || [],
      planPreset: record.plan,
      maxCloudAccounts: record.limits?.maxCloudAccounts,
      maxReportsPerMonth: record.limits?.maxReportsPerMonth,
      aiEnabled: record.features?.aiEnabled
    });
  };

  const handleUpdateTenant = async () => {
    if (!editingTenant) return;

    const values = await editForm.validateFields();
    setSaving(true);

    try {
      await apiClient.patch(`/tenants/${encodeURIComponent(editingTenant.tenantId)}`, {
        name: values.name,
        authorizedDomains: values.authorizedDomains || [],
        planPreset: values.planPreset,
        limits: {
          maxCloudAccounts: values.maxCloudAccounts,
          maxReportsPerMonth: values.maxReportsPerMonth
        },
        features: {
          aiEnabled: values.aiEnabled
        }
      });

      message.success('Tenant atualizado com sucesso.');
      setEditingTenant(null);
      await loadTenants();
    } catch (updateError: any) {
      message.error(updateError.response?.data?.message || 'Não foi possível atualizar o tenant.');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<TenantRecord> = [
    {
      title: 'Tenant',
      key: 'tenant',
      render: (_, record) => (
        <div>
          <strong>{record.name}</strong>
          <div className="admin-tenants__muted">{record.tenantId}</div>
        </div>
      )
    },
    {
      title: 'Domínios',
      key: 'authorizedDomains',
      render: (_, record) => (
        <Space wrap>
          {(record.authorizedDomains || []).map(domain => (
            <Tag key={domain}>{domain}</Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Plano',
      dataIndex: 'plan',
      key: 'plan',
      render: (plan?: string) => (
        <Tag color={planColor(plan)}>{planCatalog[plan || '']?.name || plan || '-'}</Tag>
      )
    },
    {
      title: 'Limites',
      key: 'limits',
      render: (_, record) => (
        <span>
          {record.limits?.maxCloudAccounts ?? '-'} conta(s) / {record.limits?.maxReportsPerMonth ?? '-'} relatório(s) por mês
        </span>
      )
    },
    {
      title: 'IA',
      key: 'aiEnabled',
      render: (_, record) => (
        <Tag color={record.features?.aiEnabled ? 'green' : 'red'}>
          {record.features?.aiEnabled ? 'Ativa' : 'Inativa'}
        </Tag>
      )
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: dateFormatter
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space wrap>
          <Button size="small" onClick={() => openEditModal(record)}>
            Editar
          </Button>
        </Space>
      )
    }
  ];

  if (!isPlatformAdmin) {
    return (
      <div className="admin-tenants">
        <Alert type="warning" message="Apenas platform-admins podem gerenciar tenants." showIcon />
      </div>
    );
  }

  return (
    <div className="admin-tenants">
      <div className="admin-tenants__header">
        <div>
          <span>Administração</span>
          <h1>Tenants e planos</h1>
          <p>Cadastre clientes (tenants) e controle plano, limites e recursos disponíveis para cada um.</p>
        </div>
      </div>

      {error && <Alert type="error" message={error} showIcon className="admin-tenants__alert" />}

      <Card title="Criar tenant" className="admin-tenants__card">
        <Form form={form} layout="vertical" onFinish={handleCreateTenant}>
          <div className="admin-tenants__form-grid">
            <Form.Item
              name="tenantId"
              label="ID do tenant (ex: vidalink)"
              rules={[{ required: true, message: 'Informe o ID do tenant.' }]}
            >
              <Input placeholder="vidalink" />
            </Form.Item>

            <Form.Item
              name="name"
              label="Nome (empresa/cliente)"
              rules={[{ required: true, message: 'Informe o nome.' }]}
            >
              <Input placeholder="Nome do cliente" />
            </Form.Item>

            <Form.Item name="authorizedDomains" label="Domínios autorizados">
              <Select mode="tags" placeholder="vidalink.com.br" tokenSeparators={[',']} open={false} />
            </Form.Item>

            <Form.Item
              name="planPreset"
              label="Plano"
              rules={[{ required: true, message: 'Escolha um plano.' }]}
            >
              <Select placeholder="Selecione um plano" options={planOptions} />
            </Form.Item>
          </div>

          <Button type="primary" htmlType="submit" loading={creating}>
            Criar tenant
          </Button>
        </Form>
      </Card>

      <Card title="Tenants cadastrados" className="admin-tenants__card">
        <Table
          rowKey="tenantId"
          columns={columns}
          dataSource={tenants}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title="Editar tenant"
        open={Boolean(editingTenant)}
        okText="Salvar alterações"
        cancelText="Cancelar"
        confirmLoading={saving}
        onOk={handleUpdateTenant}
        onCancel={() => setEditingTenant(null)}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="name"
            label="Nome"
            rules={[{ required: true, message: 'Informe o nome.' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="authorizedDomains" label="Domínios autorizados">
            <Select mode="tags" tokenSeparators={[',']} open={false} />
          </Form.Item>

          <Form.Item
            name="planPreset"
            label="Plano"
            rules={[{ required: true, message: 'Escolha um plano.' }]}
          >
            <Select
              options={planOptions}
              onChange={(value: string) => {
                const preset = planCatalog[value];
                if (preset) {
                  editForm.setFieldsValue({
                    maxCloudAccounts: preset.limits.maxCloudAccounts,
                    maxReportsPerMonth: preset.limits.maxReportsPerMonth,
                    aiEnabled: preset.features.aiEnabled
                  });
                }
              }}
            />
          </Form.Item>

          <Collapse ghost className="admin-tenants__advanced">
            <Collapse.Panel header="Ajustes avançados (sobrescreve o plano)" key="advanced">
              <Form.Item name="maxCloudAccounts" label="Máx. de contas cloud">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="maxReportsPerMonth" label="Máx. de relatórios por mês">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item name="aiEnabled" label="IA habilitada" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Collapse.Panel>
          </Collapse>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminTenants;
